import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import i18n from "@/i18n";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * React Error Boundary that catches render errors in its subtree and
 * displays a friendly bilingual (EN/FR) error page with retry and
 * go-home actions.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    // Log the error for debugging / monitoring
    console.error("[ErrorBoundary] Uncaught error:", error);
    console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleGoHome = (): void => {
    const lang = i18n.language?.startsWith("en") ? "en" : "fr";
    window.location.href = `/${lang}/`;
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // If a custom fallback was provided, render it instead
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const isDev = import.meta.env?.DEV ?? false;

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-16 text-center">
        {/* Animated icon */}
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-10 w-10 text-destructive animate-bounce-in" />
        </div>

        {/* Title */}
        <h1 className="mb-3 font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {i18n.t("error.something_went_wrong")}
        </h1>

        {/* Description */}
        <p className="mb-8 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
          {i18n.t("error.description")}
        </p>

        {/* Action buttons */}
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary/90 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <RefreshCw className="h-4 w-4" />
            {i18n.t("error.try_again")}
          </button>

          <button
            type="button"
            onClick={this.handleGoHome}
            className="inline-flex items-center gap-2 rounded-lg border border-input bg-background px-5 py-2.5 text-sm font-medium text-foreground shadow-sm transition-all duration-200 hover:bg-accent hover:text-accent-foreground active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Home className="h-4 w-4" />
            {i18n.t("common.go_home")}
          </button>
        </div>

        {/* Development-only error details */}
        {isDev && this.state.error && (
          <details className="mt-10 w-full max-w-2xl text-left">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
              {i18n.t("error.details")}
            </summary>
            <div className="mt-3 space-y-3 rounded-lg border border-border bg-muted/50 p-4">
              {/* Error message */}
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-destructive">
                  {i18n.t("common.error")}
                </p>
                <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs text-muted-foreground">
                  {this.state.error.message}
                </pre>
              </div>

              {/* Stack trace */}
              {this.state.error.stack && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {i18n.t("error.stack_trace")}
                  </p>
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-background p-2 text-[10px] leading-relaxed text-muted-foreground">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}

              {/* Component stack */}
              {this.state.errorInfo?.componentStack && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {i18n.t("error.component_stack")}
                  </p>
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-background p-2 text-[10px] leading-relaxed text-muted-foreground">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
